import { Search, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Combobox, type ComboOption } from '@/components/ui/combobox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { useT } from '@/i18n/I18nProvider'

// Estado dos filtros. `q` é busca livre (substring, sem acento) sobre descrição,
// categoria, conta e tags. Os demais são seleções. ALL = sem filtro.
export const ALL = '__all__'

export interface Filters {
  q: string
  accountId: string
  categoryId: string
  tag: string
  status: string // 'paid' | 'pending' | ALL
  kind: string // 'income' | 'expense' | ALL
}

export const EMPTY_FILTERS: Filters = {
  q: '',
  accountId: ALL,
  categoryId: ALL,
  tag: ALL,
  status: ALL,
  kind: ALL,
}

export function activeCount(f: Filters): number {
  let n = 0
  if (f.q.trim()) n++
  for (const v of [f.accountId, f.categoryId, f.tag, f.status, f.kind]) if (v !== ALL) n++
  return n
}

// Barra de filtros. Cada tela liga só os campos que fazem sentido nela:
// a fatura de um cartão não precisa de filtro de conta, por exemplo.
export function FilterBar({
  filters,
  onChange,
  accounts,
  categories,
  tags,
  showStatus = true,
  showKind = true,
  placeholder,
  statusOptions,
  statusAllLabel,
  onClear,
  extraActive = false,
}: {
  filters: Filters
  onChange: (f: Filters) => void
  accounts?: ComboOption[]
  categories?: ComboOption[]
  tags?: string[]
  showStatus?: boolean
  showKind?: boolean
  placeholder?: string
  /** Sobrescreve as opções de status. Em Recorrentes o estado não é pago/pendente,
   *  e sim "já incluída na transação" x "ainda não lançada". */
  statusOptions?: { value: string; label: string }[]
  statusAllLabel?: string
  /** Chamado junto do "Limpar" — a tela reseta estado próprio (ex.: o período). */
  onClear?: () => void
  /** Estado extra da tela que também deve mostrar/acionar o "Limpar" (ex.: período ≠ mês). */
  extraActive?: boolean
}) {
  const { t } = useT()
  const set = <K extends keyof Filters>(k: K, v: Filters[K]) => onChange({ ...filters, [k]: v })
  const n = activeCount(filters)
  // Defaults traduzidos: se a tela não passar o placeholder/rótulo por prop, usa o texto do idioma.
  const placeholderText = placeholder ?? t('dlg.search')
  const statusAllText = statusAllLabel ?? t('dlg.filter.allStatus')

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative min-w-[200px] flex-1">
        <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
        <Input
          value={filters.q}
          onChange={(e) => set('q', e.target.value)}
          placeholder={placeholderText}
          aria-label={placeholderText}
          className="h-9 pl-8"
        />
      </div>

      {accounts && (
        <Combobox
          className="w-[180px]"
          options={[{ value: ALL, label: t('dlg.filter.allAccounts') }, ...accounts]}
          value={filters.accountId}
          onChange={(v) => set('accountId', v)}
          placeholder={t('dlg.account')}
          searchPlaceholder={t('dlg.searchAccount')}
          aria-label={t('dlg.filter.byAccount')}
        />
      )}

      {categories && (
        <Combobox
          className="w-[190px]"
          options={[{ value: ALL, label: t('dlg.filter.allCategories') }, ...categories]}
          value={filters.categoryId}
          onChange={(v) => set('categoryId', v)}
          placeholder={t('dlg.category')}
          searchPlaceholder={t('dlg.searchCategory')}
          aria-label={t('dlg.filter.byCategory')}
        />
      )}

      {tags && tags.length > 0 && (
        <Select value={filters.tag} onValueChange={(v) => set('tag', v)}>
          <SelectTrigger className="h-9 w-[140px]">
            <SelectValue placeholder={t('dlg.filter.tag')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('dlg.filter.allTags')}</SelectItem>
            {tags.map((t) => (
              <SelectItem key={t} value={t}>
                {t}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {showKind && (
        <Select value={filters.kind} onValueChange={(v) => set('kind', v)}>
          <SelectTrigger className="h-9 w-[130px]">
            <SelectValue placeholder={t('dlg.filter.type')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('dlg.filter.bothKinds')}</SelectItem>
            <SelectItem value="income">{t('dlg.filter.onlyIncome')}</SelectItem>
            <SelectItem value="expense">{t('dlg.filter.onlyExpense')}</SelectItem>
          </SelectContent>
        </Select>
      )}

      {showStatus && (
        <Select value={filters.status} onValueChange={(v) => set('status', v)}>
          <SelectTrigger className="h-9 w-[150px]">
            <SelectValue placeholder={t('dlg.status')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{statusAllText}</SelectItem>
            {(
              statusOptions ?? [
                { value: 'paid', label: t('dlg.paid') },
                { value: 'pending', label: t('dlg.pending') },
              ]
            ).map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {(n > 0 || extraActive) && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            onChange(EMPTY_FILTERS)
            onClear?.()
          }}
          className="h-9"
        >
          <X className="size-4" /> {t('dlg.filter.clear')}
          {n > 0 && (
            <Badge variant="secondary" className="ml-1 px-1.5 text-[10px]">
              {n}
            </Badge>
          )}
        </Button>
      )}
    </div>
  )
}
